import React, { useState, useEffect } from 'react';
import { Input, Textarea } from '@heroui/input';
import { Button } from '@heroui/react';
import { Drawer, DrawerContent, DrawerHeader, DrawerBody, DrawerFooter } from '@heroui/drawer';
import useIsMobile from '../hooks/useIsMobile';
import { btnAccent, btnGhost, label as labelCls } from '@/components/ui/design';

const fieldWrap = 'rounded-2xl bg-grey99 shadow-none ring-1 ring-black/[0.04] data-[hover=true]:bg-grey95 group-data-[focus=true]:bg-grey99 group-data-[focus=true]:ring-2 group-data-[focus=true]:ring-primary/50 dark:bg-white/[0.06] dark:ring-white/10 dark:data-[hover=true]:bg-white/10 dark:group-data-[focus=true]:bg-white/[0.06] dark:group-data-[focus=true]:ring-nightAccent/60';
const fieldText = 'text-base text-grey10 placeholder:text-grey60 dark:text-white dark:placeholder:text-nightMuted';

interface FAQ {
    question: string;
    answer: string;
}

interface FAQModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (faq: FAQ) => void;
    editingFaq?: FAQ | null;
    loading?: boolean;
}

export default function FAQModal({ isOpen, onClose, onSave, editingFaq, loading = false }: FAQModalProps) {
    const [faq, setFaq] = useState<FAQ>({ question: '', answer: '' });
    const [error, setError] = useState('');

    // Reset form when modal opens/closes or when editing FAQ changes
    useEffect(() => {
        if (isOpen) {
            if (editingFaq) {
                setFaq(editingFaq);
            } else {
                setFaq({ question: '', answer: '' });
            }
            setError('');
        }
    }, [isOpen, editingFaq]);

    const handleSave = () => {
        if (!faq.question.trim()) {
            setError('Please enter a question');
            return;
        }
        if (!faq.answer.trim()) {
            setError('Please enter an answer');
            return;
        }

        setError('');
        onSave(faq);
    };

    const handleClose = () => {
        setFaq({ question: '', answer: '' });
        setError('');
        onClose();
    };

    const isMobile = useIsMobile();

    const fieldClassNames = {
        inputWrapper: `min-h-14 ${fieldWrap}`,
        input: fieldText,
    };

    return (
        <Drawer
            isOpen={isOpen}
            onClose={handleClose}
            placement="right"
            size={isMobile ? 'full' : 'lg'}
            scrollBehavior="inside"
            isDismissable={!loading}
            hideCloseButton
            classNames={{
                wrapper: isMobile ? 'z-[100000]' : 'top-[80px] h-[calc(100dvh-80px)] z-[100000]',
                base: isMobile ? 'bg-white dark:bg-nightSurface h-[100dvh] my-0 z-[100000]' : 'bg-white dark:bg-nightSurface h-[calc(100dvh-80px)] my-0 z-[100000] rounded-l-[28px] ring-1 ring-black/[0.06] dark:ring-white/10',
                backdrop: isMobile ? 'z-[100000] bg-black/50' : 'top-[80px] h-[calc(100dvh-80px)] z-[100000] bg-black/50',
            }}
        >
            <DrawerContent>
                <DrawerHeader className="flex items-center justify-between px-6 pt-6 pb-4">
                    <h2 className="text-[28px] font-bold leading-[1] tracking-[-0.04em] text-grey10 dark:text-white">
                        {editingFaq ? 'Edit FAQ' : 'New FAQ'}
                    </h2>
                    <button
                        type="button"
                        onClick={handleClose}
                        disabled={loading}
                        className="w-10 h-10 flex items-center justify-center rounded-full ring-1 ring-grey90 dark:ring-white/15 text-grey10 dark:text-white hover:bg-grey99 dark:hover:bg-white/[0.06] transition-colors flex-shrink-0 disabled:opacity-50"
                        aria-label="Close"
                    >
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                            <path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                        </svg>
                    </button>
                </DrawerHeader>

                <DrawerBody className="px-6 py-2 gap-6">
                    {error && (
                        <div className="rounded-full bg-error95 px-4 py-2.5 text-sm font-semibold text-error40 dark:bg-error50/15 dark:text-error70">
                            {error}
                        </div>
                    )}

                    <div>
                        <label className={labelCls}>
                            Question <span className="text-error50">*</span>
                        </label>
                        <Input
                            value={faq.question}
                            onChange={(e) => setFaq({ ...faq, question: e.target.value })}
                            placeholder="Question..."
                            className="w-full"
                            classNames={fieldClassNames}
                            disabled={loading}
                        />
                    </div>

                    <div>
                        <label className={labelCls}>
                            Answer <span className="text-error50">*</span>
                        </label>
                        <Textarea
                            value={faq.answer}
                            onChange={(e) => setFaq({ ...faq, answer: e.target.value })}
                            placeholder="Answer..."
                            className="w-full"
                            classNames={fieldClassNames}
                            rows={4}
                            disabled={loading}
                        />
                    </div>
                </DrawerBody>

                <DrawerFooter className="gap-2 px-6 pt-4 pb-6">
                    <Button
                        onPress={handleClose}
                        variant="light"
                        isDisabled={loading}
                        className={`${btnGhost} bg-transparent`}
                    >
                        Cancel
                    </Button>
                    <Button
                        onPress={handleSave}
                        isLoading={loading}
                        className={btnAccent}
                    >
                        {editingFaq ? 'Update FAQ' : 'Add FAQ'}
                    </Button>
                </DrawerFooter>
            </DrawerContent>
        </Drawer>
    );
}
